import React from 'react';
import { Calendar, Moon, Star, Sun, Snowflake, CheckCircle2, ArrowLeft } from 'lucide-react';

const seasons = [
  {
    icon: Moon,
    season: 'رمضان المبارك',
    title: 'عمرة رمضان من الرياض',
    description: 'أجواء إيمانية خاصة بأداء العمرة في رمضان. ت享受 مضاعفة الأجر والثواب كما ورد في الحديث النبوي الشريف. نوفر رحلات يومية من الرياض طوال شهر رمضان بباقات مخصصة.',
    features: ['رحلات يومية طوال رمضان', 'فنادق قريبة من الحرم', 'إعدادات خاصة ليالي رمضان'],
    highlight: true,
    badge: 'الأكثر طلباً',
  },
  {
    icon: Star,
    season: 'أيام الحج',
    title: 'عمرة ما بعد الحج',
    description: 'استغل فرصة وجود الحجاج في منى وعرفات واحجز عمرة هادئة بعد انتهاء موسم الحج. أسعار مميزة وفنادق متوفرة بأعداد كبيرة.',
    features: ['أسعار منخفضة بعد الحج', 'فنادق متوفرة بسهولة', 'رحلات مرنة من الرياض'],
    highlight: false,
  },
  {
    icon: Sun,
    season: 'الصيف',
    title: 'عمرة صيفية من الرياض',
    description: 'اقضِ عطلتك الصيفية بأداء العمرة مع العائلة. باقات خاصة للعائلات بغرف خاصة ومساحات واسعة مناسبة للأطفال والكبار.',
    features: ['باقات عائلية خاصة', 'غرف بسعة 4-5 أسرّة', 'مرونة في التواريخ'],
    highlight: false,
  },
  {
    icon: Snowflake,
    season: 'الشتاء',
    title: 'عمرة شتوية مريحة',
    description: 'الجو المعتدل في مكة والمدينة خلال الشتاء يجعل تجربة العمرة أكثر راحة. رحلات منتظمة من الرياض مع باقات اقتصادية مناسبة.',
    features: ['طقس معتدل ومريح', 'أسعار اقتصادية', 'رحلات منتظمة يومياً'],
    highlight: false,
  },
];

export default function SeasonalPackages() {
  return (
    <section id="seasonal" className="py-12 md:py-20 bg-gradient-to-br from-brown-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-flex items-center gap-2 bg-brown-100 text-brown-700 px-4 py-2 rounded-full text-sm font-cairo font-medium mb-4">
            <Calendar size={16} />
            باقات موسمية
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-amiri font-bold text-gray-900 mb-4 md:mb-6">
            باقات العمرة الموسمية من الرياض
          </h2>
          <p className="text-gray-600 font-cairo max-w-3xl mx-auto text-sm md:text-lg leading-relaxed">
            اختر الوقت المناسب لرحلتك — نقدم باقات مخصصة لكل موسم تناسب احتياجاتك وميزانيتك. 
            من رحلات رمضان المباركة إلى باقات الصيف العائلية، سدرة المنتهى معك طوال العام.
          </p>
        </div>

        {/* Seasons Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {seasons.map((season, index) => {
            const Icon = season.icon;
            return (
              <div
                key={index}
                className={`relative rounded-2xl p-5 md:p-8 transition-all duration-300 hover:shadow-xl group ${
                  season.highlight
                    ? 'bg-gradient-to-br from-brown-700 to-brown-900 text-white ring-2 ring-gold-400 shadow-lg'
                    : 'bg-white border border-gray-100 hover:border-brown-200'
                }`}
              >
                {season.badge && (
                  <div className="absolute top-0 left-0 bg-gold-500 text-white px-4 py-1 rounded-bl-xl rounded-tr-xl text-xs font-cairo font-bold">
                    {season.badge}
                  </div>
                )}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform ${
                    season.highlight ? 'bg-gold-500' : 'bg-brown-100'
                  }`}>
                    <Icon className={`w-7 h-7 ${season.highlight ? 'text-white' : 'text-brown-600'}`} />
                  </div>
                  <div>
                    <span className={`text-xs font-cairo font-medium ${season.highlight ? 'text-gold-300' : 'text-brown-500'}`}>
                      {season.season}
                    </span>
                    <h3 className="text-xl font-cairo font-bold">{season.title}</h3>
                  </div>
                </div>
                <p className={`font-cairo leading-relaxed mb-4 ${season.highlight ? 'text-brown-100' : 'text-gray-600'}`}>
                  {season.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {season.features.map((feature, featureIndex) => (
                    <span key={featureIndex} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-cairo ${
                      season.highlight
                        ? 'bg-white/10 text-gold-300'
                        : 'bg-brown-50 text-brown-700'
                    }`}>
                      <CheckCircle2 size={12} />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-gold-500 to-gold-600 rounded-3xl p-8 md:p-12 text-center text-brown-900">
          <h3 className="text-2xl md:text-3xl font-amiri font-bold mb-4">
            احجز رحلة عمرتك الآن من الرياض
          </h3>
          <p className="text-brown-800/80 font-cairo max-w-2xl mx-auto mb-8 leading-relaxed">
            لا تنتظر الفرصة — تواصل معنا عبر واتساب وسنرتب لك رحلة عمرة مريحة ومباركة. 
            رد سريع وتأكيد فوري على التوفر والأسعار.
          </p>
          <a
            href="https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن باقة عمرة موسمية من الرياض"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brown-800 text-white px-8 py-4 rounded-xl font-cairo font-bold text-lg hover:bg-brown-900 transition-all shadow-xl"
          >
            احجز عبر واتساب
            <ArrowLeft size={20} />
          </a>
        </div>
      </div>
    </section>
  );
}
